import * as THREE from 'three';
import type { GameContext } from '../core/context';
import type { V2 } from '../core/math';
import type { System } from '../core/system';
import { kerbClearance } from '../peds/idleSites';
import type { Quality } from '../render/post';
import { onQualityTier } from '../render/qualityTier';
import type { ParticleService } from '../vehicles/fx/particles';
import type { Pellet, PelletHit } from '../weapons/types';
import type { WeaponId } from '../weapons/weaponData';
import type { BuildingLot, BuildingStyle, CityLayout } from '../world/types';
import { PAVE_Y } from '../world/view/ground';

/**
 * The impact kit (order 719, after the weapon view): what a round does where it lands, by surface.
 * Every shot (the player's `weaponFired`, an officer's `policeShot`, another player's `netShotTraced`)
 * is sorted per pellet into a surface class from the trace's static tag or body (`classifySurface`):
 * brick / plaster / stone (buildings and walls, by the lot's style), glass (shop and pub fronts, phone
 * boxes, a car window now and then), metal (lamp posts, railings, bollards, pillar boxes, scrap), a
 * car's panel, the ground (a round that found nothing skips off the tarmac at the end of its run) and
 * a person (a puff of coat dust: no blood, by decision). Each class gets
 *   - a mark from one 4 × 2 canvas atlas, drawn by ONE instanced mesh with a per-instance tile index
 *     (brick hole with its dust ring and chipped edge, plaster hole, metal dent with a bright rim, glass
 *     star crack, pellet pock cluster for the sawn-off, pellet dents, tarmac scuff, car bullet hole): a
 *     ring buffer capped per tier, depth-offset off the wall. Car holes are kept in the car's frame and
 *     re-posed every frame from the interpolated body, so they ride with it and go when the car goes
 *     (despawn) or is repaired; window panes craze through the car's own damage (`paneState`).
 *   - surface-tinted sparks / dust through the particle service (brick-red dust, white plaster, grey
 *     stone, bright metal sparks, glass glints and shards).
 *   - a `shotImpact` event for the audio system (per-surface thud / ping / tick / thunk / skip, with an
 *     occasional ricochet whine).
 * Cosmetic only: never touches simulation state, no protocol change.
 */

export type ImpactSurface = 'brick' | 'plaster' | 'stone' | 'metal' | 'glass' | 'vehicle' | 'ground' | 'body' | 'none';

declare module '../core/events' {
  interface GameEvents {
    /** A shot landed: the surface most of its pellets found, where, and whether one sang off it (audio). */
    shotImpact: { surface: ImpactSurface; pos: V2; weapon: WeaponId; pellets: number; ricochet: boolean };
  }
}

declare module '../core/hooks' {
  interface BhHooks {
    /** Impact kit state: live marks (on walls / on cars), the atlas tile of each, and where they are (tests, review shots). */
    impactFxState(): { live: number; car: number; tiles: number[]; at: { x: number; y: number; z: number; tile: number }[] };
  }
}

/** Atlas tiles (4 × 2). */
export const TILE = { hole: 0, dent: 1, star: 2, pocks: 3, scuff: 4, carHole: 5, plasterHole: 6, pockDents: 7 } as const;
export type Tile = (typeof TILE)[keyof typeof TILE];

/** Marks kept per tier (one instanced draw call whatever the count). */
export const DECAL_CAP: Record<Quality, number> = { low: 32, medium: 64, high: 96 };
const DECAL_MAX = 96;
const DECAL_LIFE = 45;
/** Shots are drawn at chest height (weaponView SHOT_Y); a car's flank is lower. */
const SHOT_Y = 1.25;
const CAR_Y = 0.82;
/** Chance a round on a car found its window rather than a panel: a glass tick, no hole (the pane crazes through the car's damage). */
const CAR_GLASS_SHARE = 0.25;

/** Styles whose street front is mostly glass at chest height (a round on that face stars a window). */
const GLASS_FRONTS = new Set<BuildingStyle>(['shopfront', 'pub', 'cafe', 'club', 'bank']);
/** Rendered or painted fronts (pale plaster dust) and dressed-stone / concrete ones (grey chips). */
const PLASTER_STYLES = new Set<BuildingStyle>(['club', 'office']);
const STONE_STYLES = new Set<BuildingStyle>(['bank', 'church', 'chapel', 'station', 'flats']);

/** Surface a round found in a building lot: its glass front when hit on that face, else by the lot's style. */
export function buildingSurface(lot: Pick<BuildingLot, 'style' | 'angle'>, normal: V2): ImpactSurface {
  // The front faces +Z in the lot's frame (heading convention): the world normal of that face.
  const fx = Math.sin(lot.angle);
  const fz = Math.cos(lot.angle);
  if (GLASS_FRONTS.has(lot.style) && normal.x * fx + normal.z * fz > 0.7) return 'glass';
  if (PLASTER_STYLES.has(lot.style)) return 'plaster';
  if (STONE_STYLES.has(lot.style)) return 'stone';
  return 'brick';
}

/**
 * Surface class of one pellet from what the trace reports: the hit kind, the static's tag (buildings
 * are resolved through `building`, by lot id) and the physics body kind. Unknown statics are brick;
 * the boundary wall is nothing (it is not there to see).
 */
export function classifySurface(hit: PelletHit, label: string | null | undefined, building?: (lotId: number) => ImpactSurface | undefined): ImpactSurface {
  if (hit === 'target') return 'body';
  if (hit === 'vehicle') return 'vehicle';
  if (hit === 'none') return 'ground';
  if (!label) return 'brick';
  const i = label.indexOf(':');
  const kind = i < 0 ? label : label.slice(0, i);
  const sub = i < 0 ? '' : label.slice(i + 1);
  switch (kind) {
    case 'building': {
      const id = Number(sub);
      return (Number.isFinite(id) && building?.(id)) || 'brick';
    }
    case 'wall':
      return sub === 'railing' || sub === 'palisade' || sub === 'corrugated' || sub === 'sprayBay' ? 'metal' : sub === 'quayEdge' ? 'stone' : 'brick';
    case 'prop':
      if (sub === 'phoneBox') return 'glass';
      if (sub === 'tree' || sub === 'yew' || sub === 'bench') return 'brick';
      if (sub === 'grave' || sub === 'mooring') return 'stone';
      return 'metal';
    case 'scrap':
      return 'metal';
    case 'boundary':
      return 'none';
    default:
      return 'brick';
  }
}

/** The surface most pellets of a shot found (the sound is one per shot). */
export function dominantSurface(surfaces: readonly ImpactSurface[]): ImpactSurface {
  const n = new Map<ImpactSurface, number>();
  let best: ImpactSurface = 'none';
  let bestN = 0;
  for (const s of surfaces) {
    if (s === 'none') continue;
    const k = (n.get(s) ?? 0) + 1;
    n.set(s, k);
    if (k > bestN) {
      bestN = k;
      best = s;
    }
  }
  return best;
}

/** Chance a round sings off a surface (the ricochet whine). */
export function ricochetChance(surface: ImpactSurface): number {
  switch (surface) {
    case 'metal':
      return 0.3;
    case 'vehicle':
      return 0.2;
    case 'brick':
    case 'stone':
    case 'ground':
      return 0.14;
    default:
      return 0;
  }
}

/** World point and facing of a mark into a car's frame (heading convention: local +Z turned by `angle` is world (sin a, cos a)). */
export function toCarFrame(car: { x: number; z: number; angle: number }, p: V2, normal: V2): { x: number; z: number; yaw: number } {
  const c = Math.cos(car.angle);
  const s = Math.sin(car.angle);
  const dx = p.x - car.x;
  const dz = p.z - car.z;
  const nx = normal.x * c - normal.z * s;
  const nz = normal.x * s + normal.z * c;
  return { x: dx * c - dz * s, z: dx * s + dz * c, yaw: Math.atan2(nx, nz) };
}

/**
 * The 4 × 2 mark atlas, 64 px tiles, drawn in neutral tones (the instance colour tints: brick red, pale
 * plaster, grey stone, a car's paint). Row 0: masonry hole, metal dent, glass star, pellet pocks. Row 1:
 * tarmac scuff, car hole, plaster hole, pellet dents.
 */
export function makeImpactAtlas(doc: Document = document): HTMLCanvasElement {
  const T = 64;
  const cv = doc.createElement('canvas');
  cv.width = T * 4;
  cv.height = T * 2;
  const g = cv.getContext('2d')!;
  let seed = 11;
  const rnd = (): number => ((seed = (seed * 16807) % 2147483647) / 2147483647);
  const at = (tile: number, draw: (cx: number, cy: number) => void): void => {
    g.save();
    g.beginPath();
    g.rect((tile % 4) * T, Math.floor(tile / 4) * T, T, T);
    g.clip();
    draw((tile % 4) * T + T / 2, Math.floor(tile / 4) * T + T / 2);
    g.restore();
  };
  // A ragged blob: a polygon wobbling around r.
  const blob = (cx: number, cy: number, r: number, wobble: number, fill: string): void => {
    g.beginPath();
    const n = 14;
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2;
      const rr = r * (1 - wobble + rnd() * wobble * 2);
      const x = cx + Math.cos(a) * rr;
      const y = cy + Math.sin(a) * rr;
      if (i === 0) g.moveTo(x, y);
      else g.lineTo(x, y);
    }
    g.closePath();
    g.fillStyle = fill;
    g.fill();
  };
  const soft = (cx: number, cy: number, r: number, inner: string, outer = 'rgba(0,0,0,0)'): void => {
    const gr = g.createRadialGradient(cx, cy, 0, cx, cy, r);
    gr.addColorStop(0, inner);
    gr.addColorStop(1, outer);
    g.fillStyle = gr;
    g.fillRect(cx - r, cy - r, r * 2, r * 2);
  };
  // A hole in masonry: a dust halo, a ragged ring of chipped edge (lighter), the dark hole, a few pale chips.
  const masonryHole = (cx: number, cy: number, pale: number): void => {
    soft(cx, cy, 30, 'rgba(120,112,104,0.55)', 'rgba(120,112,104,0)');
    blob(cx, cy, 13, 0.3, `rgba(${150 + pale * 70},${143 + pale * 65},${132 + pale * 65},0.85)`);
    blob(cx, cy, 9.5, 0.35, 'rgba(12,10,9,0.98)');
    soft(cx, cy, 5, 'rgba(0,0,0,1)', 'rgba(0,0,0,0)');
    for (let i = 0; i < 6; i++) {
      const a = rnd() * Math.PI * 2;
      const d = 11 + rnd() * 14;
      blob(cx + Math.cos(a) * d, cy + Math.sin(a) * d, 1.5 + rnd() * 2, 0.4, `rgba(225,218,205,${0.5 + rnd() * 0.4})`);
    }
  };
  at(TILE.hole, (cx, cy) => masonryHole(cx, cy, 0));
  at(TILE.plasterHole, (cx, cy) => {
    soft(cx, cy, 31, 'rgba(235,230,220,0.7)', 'rgba(235,230,220,0)');
    masonryHole(cx, cy, 1);
  });
  // A dent in sheet metal: a shallow dark bowl, a bright crescent where the rim catches the light, a dark hole.
  at(TILE.dent, (cx, cy) => {
    soft(cx, cy, 16, 'rgba(40,38,36,0.85)', 'rgba(40,38,36,0)');
    g.beginPath();
    g.arc(cx, cy, 9.5, Math.PI * 0.95, Math.PI * 1.85);
    g.lineWidth = 2.6;
    g.strokeStyle = 'rgba(255,250,240,0.95)';
    g.stroke();
    g.beginPath();
    g.arc(cx, cy, 10.5, Math.PI * 0.05, Math.PI * 0.75);
    g.lineWidth = 1.8;
    g.strokeStyle = 'rgba(0,0,0,0.6)';
    g.stroke();
    blob(cx, cy, 4.5, 0.25, 'rgba(8,8,8,1)');
  });
  // A star crack in glass: radial cracks of uneven length, two broken rings, a small hole.
  at(TILE.star, (cx, cy) => {
    soft(cx, cy, 9, 'rgba(255,255,255,0.45)', 'rgba(255,255,255,0)');
    const n = 11;
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + rnd() * 0.3;
      const len = 14 + rnd() * 17;
      g.beginPath();
      g.moveTo(cx, cy);
      let x = cx;
      let y = cy;
      for (let k = 1; k <= 3; k++) {
        x = cx + Math.cos(a + (rnd() - 0.5) * 0.25) * ((len * k) / 3);
        y = cy + Math.sin(a + (rnd() - 0.5) * 0.25) * ((len * k) / 3);
        g.lineTo(x, y);
      }
      g.lineWidth = 2.4 - i * 0.06;
      g.strokeStyle = `rgba(255,255,255,${0.8 + rnd() * 0.2})`;
      g.stroke();
      g.lineWidth = 0.9;
      g.strokeStyle = 'rgba(0,0,0,0.4)';
      g.stroke();
    }
    for (const r of [8, 15]) {
      for (let i = 0; i < 10; i++) {
        if (rnd() < 0.3) continue;
        const a0 = (i / 10) * Math.PI * 2;
        g.beginPath();
        g.arc(cx, cy, r + rnd() * 2, a0, a0 + 0.55);
        g.lineWidth = 1.5;
        g.strokeStyle = 'rgba(255,255,255,0.85)';
        g.stroke();
      }
    }
    blob(cx, cy, 3.6, 0.3, 'rgba(5,5,5,0.95)');
  });
  // Pellet pocks: a cluster of small masonry holes with their dust.
  at(TILE.pocks, (cx, cy) => {
    soft(cx, cy, 31, 'rgba(120,112,104,0.35)', 'rgba(120,112,104,0)');
    for (let i = 0; i < 7; i++) {
      const a = rnd() * Math.PI * 2;
      const d = i === 0 ? 0 : 6 + rnd() * 20;
      const x = cx + Math.cos(a) * d;
      const y = cy + Math.sin(a) * d;
      blob(x, y, 4.8 + rnd() * 1.5, 0.3, 'rgba(150,143,132,0.8)');
      blob(x, y, 3.3 + rnd(), 0.35, 'rgba(12,10,9,0.98)');
    }
  });
  // Pellet dents in metal: the same cluster with bright rims.
  at(TILE.pockDents, (cx, cy) => {
    for (let i = 0; i < 7; i++) {
      const a = rnd() * Math.PI * 2;
      const d = i === 0 ? 0 : 6 + rnd() * 20;
      const x = cx + Math.cos(a) * d;
      const y = cy + Math.sin(a) * d;
      soft(x, y, 6, 'rgba(40,38,36,0.8)', 'rgba(40,38,36,0)');
      g.beginPath();
      g.arc(x, y, 3.4, Math.PI * 0.95, Math.PI * 1.85);
      g.lineWidth = 1.4;
      g.strokeStyle = 'rgba(255,250,240,0.95)';
      g.stroke();
      blob(x, y, 1.8, 0.3, 'rgba(8,8,8,1)');
    }
  });
  // A scuff on tarmac: a pale streak along +X (the round skipping), a dark nick where it struck.
  at(TILE.scuff, (cx, cy) => {
    g.save();
    g.translate(cx, cy);
    g.scale(1, 0.4);
    soft(0, 0, 30, 'rgba(200,196,188,0.6)', 'rgba(200,196,188,0)');
    g.restore();
    for (let i = 0; i < 5; i++) {
      blob(cx - 14 + i * 7 + rnd() * 3, cy + (rnd() - 0.5) * 5, 2 + rnd() * 2, 0.4, `rgba(215,210,200,${0.35 + rnd() * 0.3})`);
    }
    blob(cx - 12, cy, 3.5, 0.35, 'rgba(20,18,16,0.9)');
  });
  // A bullet hole in a car: a dark hole, a thin bright rim of bare metal, flaked paint around it.
  at(TILE.carHole, (cx, cy) => {
    soft(cx, cy, 20, 'rgba(60,56,52,0.5)', 'rgba(60,56,52,0)');
    for (let i = 0; i < 5; i++) {
      const a = rnd() * Math.PI * 2;
      const d = 8 + rnd() * 6;
      blob(cx + Math.cos(a) * d, cy + Math.sin(a) * d, 2 + rnd() * 2, 0.5, 'rgba(190,186,180,0.8)');
    }
    g.beginPath();
    g.arc(cx, cy, 8, 0, Math.PI * 2);
    g.lineWidth = 2.2;
    g.strokeStyle = 'rgba(245,240,230,0.95)';
    g.stroke();
    blob(cx, cy, 7, 0.3, 'rgba(8,7,6,1)');
  });
  return cv;
}

interface Slot {
  born: number;
  /** Vehicle id the mark rides on, or -1 on the world. */
  car: number;
  /** Car marks: the car's frame; world marks: unused. */
  lx: number;
  ly: number;
  lz: number;
  lyaw: number;
  sx: number;
  sy: number;
  /** The car's health when hit: climbing past it means a repair (the mark goes). */
  health: number;
}

interface Fx {
  tile: Tile;
  /** Mark size (m); sawn-off pellets use the cluster tiles at their own size. */
  size: number;
  tint: number;
}

/** Every bullet mark is drawn this much larger than its table size below, so it reads at street distance. */
export const MARK_SCALE = 1.3;

/** Per-surface mark and tint (revolver round / sawn-off pellets). */
const MARKS: Partial<Record<ImpactSurface, { shot: Fx; pellets: Fx }>> = {
  brick: { shot: { tile: TILE.hole, size: 0.16, tint: 0xb08a78 }, pellets: { tile: TILE.pocks, size: 0.55, tint: 0xb08a78 } },
  plaster: { shot: { tile: TILE.plasterHole, size: 0.19, tint: 0xf2ece2 }, pellets: { tile: TILE.pocks, size: 0.55, tint: 0xf2ece2 } },
  stone: { shot: { tile: TILE.hole, size: 0.15, tint: 0xc9c4ba }, pellets: { tile: TILE.pocks, size: 0.55, tint: 0xc9c4ba } },
  metal: { shot: { tile: TILE.dent, size: 0.13, tint: 0xffffff }, pellets: { tile: TILE.pockDents, size: 0.45, tint: 0xffffff } },
  glass: { shot: { tile: TILE.star, size: 0.6, tint: 0xffffff }, pellets: { tile: TILE.star, size: 0.75, tint: 0xffffff } },
  vehicle: { shot: { tile: TILE.carHole, size: 0.14, tint: 0xffffff }, pellets: { tile: TILE.pockDents, size: 0.5, tint: 0xffffff } },
  ground: { shot: { tile: TILE.scuff, size: 0.6, tint: 0xffffff }, pellets: { tile: TILE.scuff, size: 0.8, tint: 0xffffff } },
};

/** Dust / spark colours per surface. */
const DUST: Record<ImpactSurface, number> = { brick: 0x9a5a46, plaster: 0xe8e2d6, stone: 0xb6b0a4, metal: 0x8a8886, glass: 0xdde9f2, vehicle: 0x6a6662, ground: 0x8a8070, body: 0x6e655a, none: 0 };
const SPARK: Partial<Record<ImpactSurface, number>> = { brick: 0xd9b08a, stone: 0xd9d2c0, metal: 0xfff1c0, glass: 0xcfe8ff, vehicle: 0xffd9a0, ground: 0xe0cfa8 };

export function createImpactFxSystem(): System {
  let ctx: GameContext;
  let particles: ParticleService | undefined;
  const offs: (() => void)[] = [];
  const root = new THREE.Group();
  root.name = 'impact-fx';
  let cap = DECAL_CAP.high;

  // The atlas is painted once there is a renderer (headless games have no document); the material waits for it.
  let tex: THREE.CanvasTexture | null = null;
  const mat = new THREE.MeshLambertMaterial({ transparent: true, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 });
  // The tile index rides on the instance: the map UVs are offset into the 4 × 2 atlas in the vertex shader.
  mat.onBeforeCompile = (sh) => {
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', '#include <common>\nattribute float aTile;')
      .replace('#include <uv_vertex>', '#include <uv_vertex>\n#ifdef USE_MAP\n\tvMapUv = (uv + vec2(mod(aTile, 4.0), 1.0 - floor(aTile / 4.0))) * vec2(0.25, 0.5);\n#endif');
  };
  const geo = new THREE.PlaneGeometry(1, 1);
  const tiles = new THREE.InstancedBufferAttribute(new Float32Array(DECAL_MAX), 1);
  tiles.setUsage(THREE.DynamicDrawUsage);
  geo.setAttribute('aTile', tiles);
  const marks = new THREE.InstancedMesh(geo, mat, DECAL_MAX);
  marks.count = 0;
  marks.frustumCulled = false;
  marks.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(DECAL_MAX * 3).fill(1), 3);
  root.add(marks);
  const slots: Slot[] = [];
  for (let i = 0; i < DECAL_MAX; i++) slots.push({ born: -1, car: -1, lx: 0, ly: 0, lz: 0, lyaw: 0, sx: 1, sy: 1, health: 0 });
  let next = 0;
  let anyCar = false;

  const tmpM = new THREE.Matrix4();
  const tmpQ = new THREE.Quaternion();
  const tmpE = new THREE.Euler();
  const tmpS = new THREE.Vector3();
  const tmpP = new THREE.Vector3();
  const tmpC = new THREE.Color();
  const zero = new THREE.Matrix4().makeScale(0, 0, 0);
  const pose = { x: 0, z: 0, angle: 0 };
  const lotById = new Map<number, BuildingLot>();
  let layout: CityLayout | undefined;

  function building(id: number): ((normal: V2) => ImpactSurface) | undefined {
    layout ??= ctx.services.tryGet('layout');
    if (!lotById.size) for (const lot of layout?.buildings ?? []) lotById.set(lot.id, lot);
    const lot = lotById.get(id);
    return lot ? (normal) => buildingSurface(lot, normal) : undefined;
  }

  /** Where the tarmac (or the raised pavement) is under a point: the scuff lies on it, just above the road markings. */
  function groundY(p: V2): number {
    layout ??= ctx.services.tryGet('layout');
    return layout && kerbClearance(layout, p) > 0 ? PAVE_Y + 0.01 : 0.02;
  }

  function take(): number {
    const i = next;
    next = (next + 1) % cap;
    return i;
  }

  function place(i: number, x: number, y: number, z: number, yaw: number, flat: boolean, sx: number, sy: number): void {
    // Flat marks lie on the ground (YXZ: tipped, then turned) with the tile's +X running along `yaw`.
    tmpE.set(flat ? -Math.PI / 2 : 0, flat ? yaw - Math.PI / 2 : yaw, 0, flat ? 'YXZ' : 'XYZ');
    tmpQ.setFromEuler(tmpE);
    tmpS.set(sx, sy, 1);
    tmpP.set(x, y, z);
    tmpM.compose(tmpP, tmpQ, tmpS);
    marks.setMatrixAt(i, tmpM);
  }

  /** A mark on the world. */
  function mark(fx: Fx, p: V2, y: number, normal: V2, flat: boolean, size: number, aspect = 1): void {
    const i = take();
    const s = slots[i]!;
    s.born = ctx.state.time;
    s.car = -1;
    const yaw = Math.atan2(normal.x, normal.z);
    place(i, p.x + normal.x * 0.03, y, p.z + normal.z * 0.03, yaw, flat, size, size * aspect);
    tiles.setX(i, fx.tile);
    marks.setColorAt(i, tmpC.setHex(fx.tint));
    touch(i);
  }

  /** A mark on a car, kept in its frame. */
  function carMark(fx: Fx, car: { id: number; x: number; z: number; angle: number; health: number }, p: V2, y: number, normal: V2, size: number): void {
    const i = take();
    const s = slots[i]!;
    const l = toCarFrame(car, { x: p.x + normal.x * 0.03, z: p.z + normal.z * 0.03 }, normal);
    s.born = ctx.state.time;
    s.car = car.id;
    s.lx = l.x;
    s.ly = y;
    s.lz = l.z;
    s.lyaw = l.yaw;
    s.sx = size;
    s.sy = size;
    s.health = car.health;
    anyCar = true;
    poseCar(i, s, car);
    tiles.setX(i, fx.tile);
    marks.setColorAt(i, tmpC.setHex(fx.tint));
    touch(i);
  }

  function poseCar(i: number, s: Slot, car: { x: number; z: number; angle: number }): void {
    const c = Math.cos(car.angle);
    const sn = Math.sin(car.angle);
    place(i, car.x + s.lx * c + s.lz * sn, s.ly, car.z - s.lx * sn + s.lz * c, car.angle + s.lyaw, false, s.sx, s.sy);
  }

  function touch(i: number): void {
    marks.count = Math.max(marks.count, i + 1);
    marks.instanceMatrix.needsUpdate = true;
    tiles.needsUpdate = true;
    if (marks.instanceColor) marks.instanceColor.needsUpdate = true;
  }

  function free(i: number): void {
    const s = slots[i]!;
    s.born = -1;
    s.car = -1;
    marks.setMatrixAt(i, zero);
    marks.instanceMatrix.needsUpdate = true;
  }

  /** One shot's pellets: marks, sparks / dust, and one `shotImpact` for the ear. */
  function land(weapon: WeaponId, pellets: readonly Pellet[], remote: boolean): void {
    const vehicles = ctx.services.tryGet('vehicles');
    const shotgun = pellets.length > 1;
    const surfaces: ImpactSurface[] = [];
    const soundAt: V2 = { x: 0, z: 0 };
    let heard = false;
    pellets.forEach((p, idx) => {
      let surface = classifySurface(p.hit, p.label, (id) => building(id)?.(p.normal));
      const car = surface === 'vehicle' && p.bodyId !== null && p.bodyId !== undefined ? vehicles?.byBodyId(p.bodyId) : undefined;
      // A car's window takes a share of the rounds: glass, no hole (the pane crazes through its damage).
      const carGlass = surface === 'vehicle' && Math.random() < CAR_GLASS_SHARE;
      if (carGlass) surface = 'glass';
      surfaces.push(surface);
      if (surface === 'none') return;
      if (!heard) {
        heard = true;
        soundAt.x = p.to.x;
        soundAt.z = p.to.z;
      }
      const fx = MARKS[surface];
      const y = surface === 'ground' ? groundY(p.to) : surface === 'vehicle' || carGlass ? CAR_Y + (Math.random() - 0.5) * 0.12 : SHOT_Y + (Math.random() - 0.5) * (shotgun ? 0.5 : 0.15);
      // Marks: one per revolver round; the sawn-off's spread is one cluster tile per 3 pellets (its pattern, not 6 stamps).
      if (fx && !carGlass && (!shotgun || idx % 3 === 0)) {
        const f = shotgun ? fx.pellets : fx.shot;
        const size = f.size * MARK_SCALE * (0.85 + Math.random() * 0.3);
        if (surface === 'vehicle' && car) {
          car.body.interpolate(1, pose);
          carMark(f, { id: car.id, x: pose.x, z: pose.z, angle: pose.angle, health: car.health }, p.to, y, p.normal, size);
        } else if (surface === 'ground') {
          // Flat on the tarmac, the streak running on along the shot.
          mark(f, p.to, y, { x: -p.normal.x, z: -p.normal.z }, true, size, 0.45);
        } else if (surface !== 'vehicle') {
          mark(f, p.to, y, p.normal, false, size);
        }
      }
      // Sparks and dust in the surface's colour; a shotgun's pellets share theirs (half of them puff).
      if (!particles || (shotgun && idx % 2 === 1)) return;
      const at = { x: p.to.x, y, z: p.to.z };
      const dir = { x: p.normal.x, z: p.normal.z };
      const k = remote ? 0.8 : 1;
      switch (surface) {
        case 'brick':
        case 'plaster':
        case 'stone':
          particles.emit('dust', at, { count: 3, intensity: 0.5 * k, color: DUST[surface] });
          particles.emit('sparks', at, { count: 2, dir, intensity: 0.35 * k, color: SPARK[surface] ?? 0xd9b08a });
          break;
        case 'metal':
          particles.emit('sparks', at, { count: 6, dir, intensity: 0.8 * k, color: SPARK.metal });
          particles.emit('dust', at, { count: 1, intensity: 0.25 * k, color: DUST.metal });
          break;
        case 'glass':
          particles.emit('glass', at, { count: 4, dir, intensity: 0.6 * k, color: DUST.glass });
          particles.emit('sparks', at, { count: 2, dir, intensity: 0.4 * k, color: SPARK.glass });
          break;
        case 'vehicle':
          particles.emit('sparks', at, { count: 4, dir, intensity: 0.6 * k, color: SPARK.vehicle });
          particles.emit('dust', at, { count: 1, intensity: 0.3 * k, color: DUST.vehicle });
          break;
        case 'ground':
          particles.emit('dust', at, { count: 2, intensity: 0.4 * k, color: DUST.ground });
          particles.emit('sparks', at, { count: 1, dir, intensity: 0.3 * k, color: SPARK.ground });
          break;
        case 'body':
          // A puff of coat dust, not blood (the 1970 crime-film look keeps it bloodless).
          particles.emit('dust', { x: p.to.x, y: SHOT_Y, z: p.to.z }, { count: 3, intensity: 0.45 * k, color: DUST.body });
          break;
      }
    });
    const surface = dominantSurface(surfaces);
    if (surface !== 'none' && surface !== 'body' && heard) {
      ctx.events.emit('shotImpact', { surface, pos: soundAt, weapon, pellets: pellets.length, ricochet: Math.random() < ricochetChance(surface) });
    }
  }

  return {
    id: 'impact-fx',
    order: 719,
    init(c) {
      ctx = c;
      c.scene.add(root);
      if (c.render) {
        tex = new THREE.CanvasTexture(makeImpactAtlas());
        tex.colorSpace = THREE.SRGBColorSpace;
        tex.minFilter = THREE.LinearMipmapLinearFilter;
        tex.magFilter = THREE.LinearFilter;
        tex.anisotropy = 2;
        mat.map = tex;
        mat.needsUpdate = true;
      }
      offs.push(
        onQualityTier((q) => {
          cap = DECAL_CAP[q];
          for (let i = cap; i < DECAL_MAX; i++) if (slots[i]!.born >= 0) free(i);
          if (next >= cap) next = 0;
        }),
        c.events.on('weaponFired', (e) => land(e.weapon, e.pellets, false)),
        c.events.on('netShotTraced', (e) => land(e.weapon, e.pellets, true)),
        // An officer's round: into the player (coat dust) or off down the street (the tarmac at its end).
        c.events.on('policeShot', (e) => {
          const d = Math.hypot(e.to.x - e.from.x, e.to.z - e.from.z) || 1;
          const normal = { x: (e.from.x - e.to.x) / d, z: (e.from.z - e.to.z) / d };
          land('revolver', [{ to: e.to, hit: e.hit ? 'target' : 'none', normal }], true);
        }),
      );
      c.hooks.register('impactFxState', () => {
        const out: number[] = [];
        const at: { x: number; y: number; z: number; tile: number }[] = [];
        let car = 0;
        for (let i = 0; i < DECAL_MAX; i++) {
          if (slots[i]!.born < 0) continue;
          out.push(tiles.getX(i));
          if (slots[i]!.car >= 0) car++;
          marks.getMatrixAt(i, tmpM);
          tmpP.setFromMatrixPosition(tmpM);
          at.push({ x: +tmpP.x.toFixed(2), y: +tmpP.y.toFixed(2), z: +tmpP.z.toFixed(2), tile: tiles.getX(i) });
        }
        return { live: out.length, car, tiles: out, at };
      });
    },
    start() {
      particles = ctx.services.tryGet('particles');
    },
    update(_dt, alpha) {
      particles ??= ctx.services.tryGet('particles');
      const time = ctx.state.time;
      const vehicles = anyCar ? ctx.services.tryGet('vehicles') : undefined;
      let carLeft = false;
      for (let i = 0; i < marks.count; i++) {
        const s = slots[i]!;
        if (s.born < 0) continue;
        if (time - s.born > DECAL_LIFE) {
          free(i);
          continue;
        }
        if (s.car < 0) continue;
        const v = vehicles?.get(s.car);
        // Gone with the car (despawn) or with a repair (health back up).
        if (!v || v.health > s.health + 1e-3) {
          free(i);
          continue;
        }
        carLeft = true;
        v.body.interpolate(alpha, pose);
        poseCar(i, s, pose);
        marks.instanceMatrix.needsUpdate = true;
      }
      anyCar = carLeft;
    },
    dispose() {
      for (const off of offs) off();
      offs.length = 0;
      geo.dispose();
      mat.dispose();
      tex?.dispose();
      root.removeFromParent();
    },
  };
}
